import React, { useRef, useState, useCallback, useEffect } from "react";
import { SelectComponent } from "../../../components";
import Webcam from "react-webcam";
import Styles from "../styles/StylesForm";
import { customModal, topModal } from "../../../components/modals/utils";
import { fonts } from "../../../config";
import { alerts, hooks } from "../../../utils";
import { FormHelperText } from "@mui/material";
import { VideocamOff } from "@mui/icons-material";
import { api } from "../../../services";
import { FormFull } from "form-full";
import { FaceDetector, FilesetResolver } from "@mediapipe/tasks-vision";

function WebcamModal({ onSubmit, data, userId = "" }: { onSubmit: any; data?: any; userId?: String | null }) {
  const { loading, call } = hooks.useRequest();
  const facialFormat = data?.find(
    item => item.type === "FACIAL_FORMAT"
  );


  const webcamRef = useRef<
    | (Webcam & {
      getScreenshot: () => string;
    })
    | null
  >(null);
  const [imgSrc, setImgSrc] = useState<{ src: string; name: string } | null>(null);
  const [resImage, setResImage] = useState<number>(1);
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);
  const [errorImageValidation, setErrorImageValidation] = useState("");
  const [cameraLoading, setCameraLoading] = useState<boolean>(true);
  const [loadingValidation, setLoadingValidation] = useState<boolean>(false);
  const [faceStatus, setFaceStatus] = useState<{
    isValid: boolean;
    messages: string[];
  }>({ isValid: false, messages: ["Posicione seu rosto na área indicada"] });
  const [countdown, setCountdown] = useState<number | null>(null);
  const [faceDetectionKey, setFaceDetectionKey] = useState(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const faceDetectorRef = useRef<FaceDetector | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const invalidFramesRef = useRef(0);
  const INVALID_THRESHOLD = 5;

  const [resolution, setResolution] = useState<any>({});


  const cropImageEnabled = data.find((element) => element.type === "CROP_IMAGE_ENABLED")?.value === "true" || true;
  const cropImageMinWidth = Number(data.find((element) => element.type === "CROP_IMAGE_MIN_WIDTH")?.value || 200);
  const cropImageMinHeight = Number(data.find((element) => element.type === "CROP_IMAGE_MIN_HEIGHT")?.value || 300);
  const maxImageSize = Number(data.find((element) => element.type === "CROP_IMAGE_MAX_SIZE")?.value || 100); // in KB
  const cropTopBottomPx = Number(data.find((element) => element.type === "TOP_IMAGE_CROP_PIXELS")?.value || 0);

  useEffect(() => {
    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === "videoinput");
      if (videoDevices.length > 0) {
        const constraints = {
          video: {
            facingMode: "user",
            deviceId: videoDevices[0].deviceId,
          },
        };
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          const track = stream.getVideoTracks()[0];
          const settings = track.getSettings();
          setResolution({
            width: document.documentElement.clientWidth,
            height: document.documentElement.clientHeight,
            ratio: 1,
          });
        } catch (error) {
          console.error("Error accessing camera:", error);
        }
      }
    };

    getDevices();
  }, []);

  useEffect(() => {
    if (!isCameraEnabled || !webcamRef.current?.video || imgSrc) return;

    const videoElement = webcamRef.current.video;
    let isActive = true;

    const validateFace = (detections: any[]) => {
      const messages: string[] = [];
      let isValid = true;

      if (detections.length === 0) {
        return { isValid: false, messages: ["Nenhum rosto detectado"] };
      }

      if (detections.length > 1) {
        return { isValid: false, messages: ["Múltiplos rostos detectados"] };
      }

      const detection = detections[0];
      const boundingBox = detection.boundingBox;
      const keypoints = detection.keypoints;

      // Convert from origin (top-left corner) to center coordinates (normalized 0-1)
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;
      const centerX = (boundingBox.originX + boundingBox.width / 2) / videoWidth;
      const centerY = (boundingBox.originY + boundingBox.height / 2) / videoHeight;
      const width = boundingBox.width / videoWidth;

      // Verifica se o rosto está dentro do eixo X indicado (centralizado)
      if (centerX < 0.4) {
        messages.push("Mais para a esquerda");
        isValid = false;
      } else if (centerX > 0.6) {
        messages.push("Mais para a direita");
        isValid = false;
      }

      // Verifica se o rosto está dentro do eixo Y indicado
      if (centerY < 0.40) {
        messages.push("Mais para baixo");
        isValid = false;
      } else if (centerY > 0.60) {
        messages.push("Mais para cima");
        isValid = false;
      }


      if (width < 0.35) {
        messages.push("Chegue mais perto da câmera");
        isValid = false;
      } else if (width > 0.45) {
        messages.push("Se afaste da câmera");
        isValid = false;
      }

      // Keypoints validation (indices: 0=rightEye, 1=leftEye, 2=noseTip)
      if (keypoints && keypoints.length >= 3) {
        const rightEye = keypoints[0];
        const leftEye = keypoints[1];
        const nose = keypoints[2];

        if (rightEye && leftEye && nose) {
          const eyeMidX = (rightEye.x + leftEye.x) / 2;
          const eyeDistance = Math.abs(rightEye.x - leftEye.x);
          const noseToEyeMidX = Math.abs(nose.x - eyeMidX);

          // Check yaw (horizontal head rotation)
          if (eyeDistance > 0 && noseToEyeMidX / eyeDistance > 0.15) {
            messages.push("Olhe reto para a câmera");
            isValid = false;
          }

          // Check pitch (vertical head rotation)
          const eyeMidY = (rightEye.y + leftEye.y) / 2;
          const noseToEyeDistY = nose.y - eyeMidY;

          if (noseToEyeDistY < eyeDistance * 0.20) {
            messages.push("Abaixe um pouco o queixo");
            isValid = false;
          } else if (noseToEyeDistY > eyeDistance * 0.55) {
            messages.push("Levante um pouco o queixo");
            isValid = false;
          }
        }
      }

      if (isValid) {
        return { isValid: true, messages: ["Perfeito! Aguarde..."] };
      }

      return { isValid, messages };
    };

    const initFaceDetector = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm"
        );

        const faceDetector = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
          },
          runningMode: "VIDEO",
          minDetectionConfidence: 0.5,
        });

        if (!isActive) {
          faceDetector.close();
          return;
        }

        faceDetectorRef.current = faceDetector;

        let lastVideoTime = -1;

        const detectFaces = () => {
          if (!isActive || !faceDetectorRef.current) return;

          // Verificar se o vídeo tem dimensões válidas antes de processar
          if (
            videoElement.readyState >= 2 &&
            videoElement.currentTime !== lastVideoTime &&
            videoElement.videoWidth > 0 &&
            videoElement.videoHeight > 0
          ) {
            try {
              const detections = faceDetectorRef.current.detectForVideo(videoElement, performance.now());
              const status = validateFace(detections.detections);
              setFaceStatus(status);
              lastVideoTime = videoElement.currentTime;
            } catch (error) {
              console.error("Face detection error:", error);
              return; // Para o loop em caso de erro
            }
          }

          animationFrameRef.current = requestAnimationFrame(detectFaces);
        };

        detectFaces();
      } catch (error) {
        console.error("Failed to initialize face detector:", error);
      }
    };

    initFaceDetector();

    return () => {
      isActive = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (faceDetectorRef.current) {
        faceDetectorRef.current.close();
        faceDetectorRef.current = null;
      }
    };
  }, [isCameraEnabled, faceDetectionKey, imgSrc]);

  useEffect(() => {
    if (faceStatus.isValid) {
      invalidFramesRef.current = 0;
      if (countdown === null) {
        setCountdown(3);
      }
    } else if (!faceStatus.isValid) {
      setCountdown(null);
    }
    else {
      invalidFramesRef.current++;
      if (countdown !== null && invalidFramesRef.current >= INVALID_THRESHOLD) {
        setCountdown(null);
        if (countdownRef.current) {
          clearTimeout(countdownRef.current);
          countdownRef.current = null;
        }
      }
    }
  }, [faceStatus.isValid]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      capture();
    }

    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, [countdown]);

  const capture = useCallback(async () => {
    const finalCanvas = document.createElement("canvas");
    if (!webcamRef.current?.video) return;
    const finalCtx = finalCanvas.getContext("2d");

    const videoElement = webcamRef.current.video;
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    // Capturar em formato 4:3 (640x480)
    const targetWidth = 640;
    const targetHeight = 480;
    const targetAspect = targetWidth / targetHeight;
    const videoAspect = videoWidth / videoHeight;

    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = videoWidth;
    let sourceHeight = videoHeight;

    // Crop para manter 4:3
    if (videoAspect > targetAspect) {
      // Video é mais largo, cortar nas laterais
      sourceWidth = videoHeight * targetAspect;
      sourceX = (videoWidth - sourceWidth) / 2;
    } else {
      // Video é mais alto, cortar em cima/baixo
      sourceHeight = videoWidth / targetAspect;
      sourceY = (videoHeight - sourceHeight) / 2;
    }

    finalCanvas.width = targetWidth;
    finalCanvas.height = targetHeight;

    if (!finalCtx) return;

    // Mirror horizontally to match the webcam preview
    finalCtx.scale(-1, 1);
    finalCtx.translate(-finalCanvas.width, 0);

    // Draw the cropped area in 4:3
    finalCtx.drawImage(
      videoElement,
      sourceX, sourceY, sourceWidth, sourceHeight,
      0, 0, targetWidth, targetHeight
    );

    setImgSrc({
      src: finalCanvas.toDataURL(facialFormat === "PNG" ? "image/png" : "image/jpeg", 0.95),
      name: "user-photo",
    });
  }, [webcamRef, resImage]);

  function removerBase64EAntes(texto) {
    if (!texto) return null;
    const indiceBase64 = texto.indexOf("base64,");
    if (indiceBase64 !== -1) return texto.substring(indiceBase64 + 7);
    return texto;
  }

  const sendData = async () => {
    setLoadingValidation(true);
    const response = await api.validateImage({
      id: userId,
      image: removerBase64EAntes(imgSrc?.src),
    });
    const imageisValid = response.status !== 200 ? true : response.data.isValid;
    if (!imageisValid) {
      setErrorImageValidation(response.data.error);
      return;
    }
    setLoadingValidation(false);
    setErrorImageValidation("");
    customModal.close();
    onSubmit(imgSrc);
  };

  const renderCapturedImage = () => (
    <div style={styles.container}>
      <div style={styles.instructions}>
        <div style={styles.bulletPoint}>
          <span style={styles.bullet}>•</span> Priorize fundos simples, de preferência branco e sem luzes;
        </div>
        <div style={styles.bulletPoint}>
          <span style={styles.bullet}>•</span> Enquadre bem a região do seu rosto, acima do peitoral e ombros;
        </div>
        <div style={styles.bulletPoint}>
          <span style={styles.bullet}>•</span> Evite luzes superiores que causem projeções de sombra em seu rosto;
        </div>
      </div>
      <div
        style={{
          width: "100%",
          maxWidth: "640px",
          aspectRatio: "4 / 3",
          backgroundImage: imgSrc ? `url(${imgSrc.src})` : "",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "cover",
          borderRadius: "5px",
        }}
      ></div>
      <p
        style={{
          ...styles.errorMessage,
          visibility: errorImageValidation === "" ? "hidden" : "visible",
        }}
      >
        {" "}
        {errorImageValidation}{" "}
      </p>
      <div style={styles.actions}>
        <Styles.BackPhoto
          fullWidth={false}
          onClick={() => {
            setImgSrc(null);
            setErrorImageValidation("");
            setLoadingValidation(false);
            setFaceDetectionKey(prev => prev + 1); // Force face detection reinitialization
            setFaceStatus({ isValid: false, messages: ["Posicione seu rosto na área indicada"] });
            setCountdown(null);
          }}
        >
          {errorImageValidation !== "" ? "Tirar Nova Foto" : "voltar"}
        </Styles.BackPhoto>
        <Styles.SendForm
          disabled={errorImageValidation !== "" || loadingValidation}
          fullWidth={false}
          onClick={sendData}
          style={{
            backgroundColor: "red",
            borderColor: "red",
            borderStyle: "solid",
            borderWidth: "2px",
          }}
        >
          {errorImageValidation !== "" ? "Erro na validação" : loadingValidation ? "Validando..." : "Enviar Foto"}
        </Styles.SendForm>
      </div>
    </div>
  );



  const renderCamera = () => (
    <div style={styles.container}>
      <div style={styles.instructions}>
        <div style={styles.bulletPoint}>
          <span style={styles.bullet}>•</span> Priorize fundos simples, de preferência branco e sem luzes;
        </div>
        <div style={styles.bulletPoint}>
          <span style={styles.bullet}>•</span> Enquadre bem a região do seu rosto, acima do peitoral e ombros;
        </div>
        <div style={styles.bulletPoint}>
          <span style={styles.bullet}>•</span> Evite luzes superiores que causem projeções de sombra em seu rosto;
        </div>
      </div>

      {/* Face status feedback */}
      {isCameraEnabled && (
        <div
          style={{
            ...styles.feedbackContainer,
            ...(faceStatus.isValid ? styles.feedbackSuccess : styles.feedbackError),
          }}
        >
          {faceStatus.isValid ? "✓ " : ""}
          {faceStatus.messages.join(" • ")}
        </div>
      )}

      {!isCameraEnabled && (
        <div style={styles.cameraDisabled}>
          <VideocamOff style={styles.icon} />
        </div>
      )}

      <div
        className="webcam-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          width: "100%",
          maxWidth: "640px",
          aspectRatio: "4 / 3",
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        <Webcam
          id="webcam"
          mirrored
          screenshotQuality={resImage}
          screenshotFormat={facialFormat === "PNG" ? "image/png" : "image/jpeg"}
          videoConstraints={{
            facingMode: "user",
            aspectRatio: 4 / 3,
            width: { ideal: 640 },
            height: { ideal: 480 },
          }}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: isCameraEnabled ? "block" : "none",
          }}
          audio={false}
          ref={webcamRef}
          onUserMedia={() => setIsCameraEnabled(true)}
          onUserMediaError={() => setIsCameraEnabled(false)}
        />

        {/* Face oval overlay */}
        {isCameraEnabled && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) translateZ(0)",
              WebkitTransform: "translate(-50%, -50%) translateZ(0)",
              width: "40%",
              height: "70%",
              border: `3px solid ${faceStatus.isValid ? "#4CAF50" : "#f44336"}`,
              borderRadius: "50% / 50%",
              pointerEvents: "none",
              transition: "border-color 0.3s ease",
              zIndex: 10,
            }}
          />
        )}

        {/* Countdown overlay */}
        {countdown !== null && countdown > 0 && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) translateZ(0)",
              WebkitTransform: "translate(-50%, -50%) translateZ(0)",
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={styles.countdownNumber}>{countdown}</span>
          </div>
        )}
      </div>

      <div style={styles.actions}>
        <Styles.BackPhoto fullWidth={false} onClick={customModal.close}>
          Voltar
        </Styles.BackPhoto>
      </div>
    </div>
  );

  return imgSrc?.src ? renderCapturedImage() : renderCamera();
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    paddingInline: "20px",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
  },
  instructions: {
    marginBottom: "10px",
    marginLeft: "5px",
    textAlign: "left",
    color: "white",
  },
  bulletPoint: {
    display: "flex",
    marginBottom: "5px",
  },
  errorMessage: {
    color: "#ff0000ff",
  },
  bullet: {
    marginRight: "5px",
    fontSize: "16px",
  },
  imagePreview: {
    borderRadius: "5px",
    maxHeight: "80vh",
    minHeight: "300px",
    maxWidth: "80vw",
    height: "100%",
    width: "900px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "contain",
  },
  cameraDisabled: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#8D9B9C",
    borderRadius: "5px",
    maxHeight: "80vh",
    maxWidth: "80vw",
    height: "100%",
    width: "900px",
    minHeight: "300px",
  },
  icon: {
    fontSize: "48px",
    color: "#fff",
  },
  webcam: {
    borderRadius: "5px",
    maxWidth: "1000px",
    maxHeight: "100%",
    width: "100%",
    height: "100%",
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingTop: "10px",
    gap: "8px",
    width: "100%",
  },
  feedbackContainer: {
    padding: "10px 20px",
    borderRadius: "8px",
    marginBottom: "10px",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "14px",
  },
  feedbackSuccess: {
    backgroundColor: "rgba(76, 175, 80, 0.2)",
    color: "#4CAF50",
    border: "1px solid #4CAF50",
  },
  feedbackError: {
    backgroundColor: "rgba(244, 67, 54, 0.2)",
    color: "#f44336",
    border: "1px solid #f44336",
  },
  countdownOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  countdownNumber: {
    fontSize: "120px",
    fontWeight: "bold",
    color: "#4CAF50",
    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
    animation: "pulse 1s ease-in-out",
  },
};

export default WebcamModal;
